import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Layout from '../../components/layout/main';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    Box,
    Button,
    Chip,
    Fade,
    Grid,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CalculateRoundedIcon from '@mui/icons-material/CalculateRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CollectionsBookmarkRoundedIcon from '@mui/icons-material/CollectionsBookmarkRounded';
import LightbulbRoundedIcon from '@mui/icons-material/LightbulbRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ClearAllRoundedIcon from '@mui/icons-material/ClearAllRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ListAltRoundedIcon from '@mui/icons-material/ListAltRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import { useSnackbar } from 'notistack';
import { AuthContext } from '../../context/auth';
import { getDataFull, postData } from '../../services/ajax';
import { LoadingModalContext } from '../../components/modal/loading';

const floaty = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

const shimmer = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(13, 79, 92, 0.35); }
  50% { box-shadow: 0 0 24px 4px rgba(255, 107, 92, 0.25); }
`;

function fmtMoney(n) {
    if (n == null || n === '') return '—';
    return `$ ${Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function nuevaLineaEstudio() {
    return { key: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`, determinacion: null, veces: 1 };
}

function NhpdgdPage() {
    const theme = useTheme();
    const { usuario } = useContext(AuthContext);
    const { enqueueSnackbar } = useSnackbar();
    const loadingModal = useContext(LoadingModalContext);

    const [listaDeterminaciones, setListaDeterminaciones] = useState([]);
    const [lineasEstudio, setLineasEstudio] = useState(() => [nuevaLineaEstudio()]);
    const [resultado, setResultado] = useState(null);

    useEffect(() => {
        let cancel = false;
        (async () => {
            const res = await getDataFull('determinaciones?per_page=500&order_by=nombre&order=asc', usuario.token);
            if (cancel) return;
            if (res.error === '') {
                const items = Array.isArray(res.data) ? res.data : [];
                setListaDeterminaciones(items);
                const total = res.meta?.total;
                if (total != null && total > items.length) {
                    enqueueSnackbar(
                        `Solo se cargaron ${items.length} de ${total} estudios. Contactá al administrador.`,
                        { variant: 'warning' }
                    );
                }
            } else {
                enqueueSnackbar(res.error, { variant: 'error' });
            }
        })();
        return () => {
            cancel = true;
        };
    }, [usuario?.token, enqueueSnackbar]);

    const onGenerar = useCallback(async () => {
        const items = lineasEstudio
            .filter((l) => l.determinacion && l.determinacion.id)
            .map((l) => {
                let v = parseInt(String(l.veces), 10);
                if (Number.isNaN(v) || v < 1) v = 1;
                if (v > 999) v = 999;
                return { determinacion_id: l.determinacion.id, veces: v };
            });
        if (!items.length) {
            enqueueSnackbar('Agregá al menos un estudio y elegí cuál es', { variant: 'warning' });
            return;
        }
        loadingModal.setOpen(true);
        const res = await postData('nhpdgd/generar', { items }, usuario.token);
        loadingModal.setOpen(false);
        if (res.error) {
            enqueueSnackbar(res.error, { variant: 'error' });
            setResultado(null);
            return;
        }
        setResultado(res.data || null);
        enqueueSnackbar('Liquidación lista (referencia, no se guarda)', { variant: 'success' });
    }, [lineasEstudio, usuario?.token, loadingModal, enqueueSnackbar]);

    const setLinea = useCallback((key, patch) => {
        setLineasEstudio((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    }, []);

    const agregarFila = useCallback(() => {
        setLineasEstudio((rows) => [...rows, nuevaLineaEstudio()]);
    }, []);

    const quitarFila = useCallback((key) => {
        setLineasEstudio((rows) => {
            if (rows.length <= 1) return [nuevaLineaEstudio()];
            return rows.filter((r) => r.key !== key);
        });
    }, []);

    const ajustarVeces = useCallback((key, delta) => {
        setLineasEstudio((rows) =>
            rows.map((r) => {
                if (r.key !== key) return r;
                let v = parseInt(String(r.veces), 10);
                if (Number.isNaN(v) || v < 1) v = 1;
                v += delta;
                if (v < 1) v = 1;
                if (v > 999) v = 999;
                return { ...r, veces: v };
            })
        );
    }, []);

    const limpiarEstudios = useCallback(() => {
        setLineasEstudio([nuevaLineaEstudio()]);
        setResultado(null);
    }, []);

    const bgMesh = useMemo(
        () =>
            theme.palette.mode === 'dark'
                ? `radial-gradient(ellipse 120% 80% at 10% -10%, ${alpha('#ff6b5c', 0.12)} 0%, transparent 50%),
                   radial-gradient(ellipse 80% 60% at 90% 10%, ${alpha('#0d9488', 0.18)} 0%, transparent 45%),
                   linear-gradient(165deg, ${alpha('#0c1929', 1)} 0%, #0a1628 40%, #0d1f35 100%)`
                : `radial-gradient(ellipse 100% 70% at 0% 0%, ${alpha('#ff6b5c', 0.14)} 0%, transparent 55%),
                   radial-gradient(ellipse 90% 60% at 100% 0%, ${alpha('#0d9488', 0.12)} 0%, transparent 50%),
                   linear-gradient(180deg, #f7f4ef 0%, #eef6f4 35%, #f0f7f5 100%)`,
        [theme.palette.mode]
    );

    return (
        <Layout nombrePagina="NHPdGD — Generador">
            <Box
                sx={{
                    fontFamily: '"DM Sans", system-ui, sans-serif',
                    borderRadius: 3,
                    p: { xs: 2, md: 3 },
                    mb: 2,
                    background: bgMesh,
                    border: `1px solid ${alpha(theme.palette.divider, 0.35)}`,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(110deg, transparent 40%, rgba(255,255,255,0.04) 50%, transparent 60%)',
                        backgroundSize: '200% 100%',
                        animation: `${shimmer} 8s linear infinite`,
                        pointerEvents: 'none',
                    },
                }}
            >
                <Stack spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Box
                                sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: 2.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: `linear-gradient(135deg, ${alpha('#0d9488', 0.25)} 0%, ${alpha('#ff6b5c', 0.2)} 100%)`,
                                    animation: `${floaty} 5s ease-in-out infinite`,
                                }}
                            >
                                <ReceiptLongRoundedIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                            </Box>
                            <Box>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontFamily: '"Fraunces", Georgia, serif',
                                        fontWeight: 700,
                                        letterSpacing: '-0.02em',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    NHPdGD
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Nomenclador Hospitales Públicos — Gestión Descentralizada. Elegí los estudios realizados y obtené el código de
                                    liquidación y el arancel estimado.
                                </Typography>
                            </Box>
                        </Stack>
                        <Chip icon={<LightbulbRoundedIcon />} label="Solo generador · no guarda datos" color="default" variant="outlined" sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }} />
                    </Stack>

                    <Grid container spacing={2}>
                        {[
                            {
                                n: '1',
                                t: 'Armá la lista de estudios',
                                d: 'Elegí cada estudio del catálogo y cuántas veces se hizo. Podés repetir el mismo (por ejemplo dos hemogramas y tres coagulogramas).',
                                icon: <CollectionsBookmarkRoundedIcon />,
                            },
                            {
                                n: '2',
                                t: 'Unimos los códigos',
                                d: 'El sistema suma las cantidades de cada código (todas las 40.01 juntas, todas las 40.02, etc.).',
                                icon: <CalculateRoundedIcon />,
                            },
                            {
                                n: '3',
                                t: 'Liquidación y arancel',
                                d: 'Una sola 40.01: fórmula 40.01×1 y arancel de ese código. Dos 40.01: 1.03×1. Más cantidad: 1.05 o bloques de 8. El resto de códigos, cantidad × arancel.',
                                icon: <AutoAwesomeRoundedIcon />,
                            },
                        ].map((step) => (
                            <Grid key={step.n} size={{ xs: 12, md: 4 }}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        height: '100%',
                                        borderRadius: 2,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                                        background: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.35 : 0.85),
                                        backdropFilter: 'blur(8px)',
                                        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: `0 12px 32px ${alpha('#0d4f5c', 0.12)}`,
                                        },
                                    }}
                                >
                                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                        <Box
                                            sx={{
                                                minWidth: 36,
                                                height: 36,
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: 700,
                                                fontSize: '0.95rem',
                                                bgcolor: alpha('#0d9488', 0.2),
                                                color: 'primary.dark',
                                            }}
                                        >
                                            {step.n}
                                        </Box>
                                        <Box>
                                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'primary.main', mb: 0.5 }}>
                                                {step.icon}
                                                <Typography fontWeight={700}>{step.t}</Typography>
                                            </Stack>
                                            <Typography variant="body2" color="text.secondary">
                                                {step.d}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>

                    <Paper
                        sx={{
                            p: { xs: 1.5, md: 2 },
                            borderRadius: 3,
                            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                            animation: `${pulseGlow} 4s ease-in-out infinite`,
                        }}
                    >
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1} sx={{ mb: 0.75 }}>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="subtitle2" fontWeight={700}>
                                    Estudios a liquidar
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35, mt: 0.25 }}>
                                    Una fila por estudio; si se repite el mismo, indicá cuántas veces. Podés borrar todo para armar otro cálculo.
                                </Typography>
                            </Box>
                            <Tooltip title="Vaciar la lista y el resultado (empezar de nuevo)">
                                <Button
                                    size="small"
                                    color="inherit"
                                    variant="outlined"
                                    startIcon={<ClearAllRoundedIcon sx={{ fontSize: 18 }} />}
                                    onClick={limpiarEstudios}
                                    sx={{ flexShrink: 0, py: 0.5, px: 1, fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                                >
                                    Limpiar todo
                                </Button>
                            </Tooltip>
                        </Stack>

                        <Stack
                            divider={
                                <Box
                                    component="hr"
                                    sx={{
                                        border: 0,
                                        borderTop: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                                        m: 0,
                                    }}
                                />
                            }
                            spacing={0}
                            sx={{
                                border: `1px solid ${alpha(theme.palette.divider, 0.45)}`,
                                borderRadius: 1.5,
                                overflow: 'hidden',
                                bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.35 : 0.65),
                            }}
                        >
                            {lineasEstudio.map((linea, idx) => (
                                <Stack
                                    key={linea.key}
                                    direction={{ xs: 'column', sm: 'row' }}
                                    alignItems={{ sm: 'center' }}
                                    spacing={{ xs: 1, sm: 0.75 }}
                                    sx={{
                                        px: { xs: 1, sm: 1.25 },
                                        py: { xs: 1, sm: 0.5 },
                                        gap: { sm: 1 },
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            width: { sm: 22 },
                                            flexShrink: 0,
                                            fontWeight: 700,
                                            fontVariantNumeric: 'tabular-nums',
                                            alignSelf: { xs: 'flex-start', sm: 'center' },
                                            pt: { xs: 0, sm: 0.25 },
                                        }}
                                    >
                                        {idx + 1}.
                                    </Typography>
                                    <Autocomplete
                                        sx={{ flex: 1, minWidth: 0 }}
                                        size="small"
                                        options={listaDeterminaciones}
                                        value={linea.determinacion}
                                        onChange={(_, v) => setLinea(linea.key, { determinacion: v })}
                                        getOptionLabel={(o) => (o?.nombre ? `${o.nombre}${o.grupo?.nombre ? ` · ${o.grupo.nombre}` : ''}` : '')}
                                        isOptionEqualToValue={(a, b) => a?.id === b?.id}
                                        renderInput={(params) => (
                                            <TextField {...params} placeholder="Buscar estudio…" size="small" />
                                        )}
                                    />
                                    <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'center' } }}>
                                        <Tooltip title="Veces">
                                            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' }, mr: 0.25, minWidth: 28 }}>
                                                ×
                                            </Typography>
                                        </Tooltip>
                                        <IconButton
                                            size="small"
                                            aria-label="Menos una vez"
                                            onClick={() => ajustarVeces(linea.key, -1)}
                                            disabled={(Number(linea.veces) || 1) <= 1}
                                            sx={{ p: 0.35 }}
                                        >
                                            <RemoveRoundedIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                        <TextField
                                            value={linea.veces}
                                            onChange={(e) => {
                                                const raw = e.target.value.replace(/\D/g, '');
                                                if (raw === '') {
                                                    setLinea(linea.key, { veces: 1 });
                                                    return;
                                                }
                                                let n = parseInt(raw, 10);
                                                if (n < 1) n = 1;
                                                if (n > 999) n = 999;
                                                setLinea(linea.key, { veces: n });
                                            }}
                                            onBlur={() => {
                                                let n = parseInt(String(linea.veces), 10);
                                                if (Number.isNaN(n) || n < 1) n = 1;
                                                if (n > 999) n = 999;
                                                setLinea(linea.key, { veces: n });
                                            }}
                                            size="small"
                                            sx={{
                                                width: 48,
                                                '& .MuiInputBase-root': { py: 0 },
                                                '& .MuiInputBase-input': { textAlign: 'center', py: 0.5, fontSize: '0.8125rem' },
                                            }}
                                            inputProps={{
                                                'aria-label': 'Cantidad de veces',
                                                min: 1,
                                                max: 999,
                                                inputMode: 'numeric',
                                            }}
                                        />
                                        <IconButton size="small" aria-label="Más una vez" onClick={() => ajustarVeces(linea.key, 1)} sx={{ p: 0.35 }}>
                                            <AddRoundedIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                        <Typography variant="caption" color="text.secondary" sx={{ userSelect: 'none', ml: 0.25, display: { xs: 'none', sm: 'block' } }}>
                                            veces
                                        </Typography>
                                        <Tooltip title="Quitar esta fila">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    aria-label="Quitar estudio"
                                                    onClick={() => quitarFila(linea.key)}
                                                    disabled={lineasEstudio.length === 1 && !linea.determinacion}
                                                    sx={{ p: 0.35, ml: { xs: 'auto', sm: 0.25 } }}
                                                >
                                                    <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                </Stack>
                            ))}
                        </Stack>

                        <Button
                            variant="outlined"
                            size="small"
                            fullWidth
                            sx={{ mt: 1.5, py: 0.75, fontWeight: 600 }}
                            onClick={agregarFila}
                            startIcon={<AddRoundedIcon fontSize="small" />}
                        >
                            Agregar estudio
                        </Button>
                        <Button
                            variant="contained"
                            size="medium"
                            fullWidth
                            sx={{ mt: 1.25, py: 1.1, borderRadius: 2, fontWeight: 700 }}
                            onClick={onGenerar}
                            startIcon={<CalculateRoundedIcon />}
                        >
                            Generar código y arancel
                        </Button>
                    </Paper>

                    <Fade in={Boolean(resultado)} timeout={600}>
                        <Box>
                            {resultado && (
                                <Stack spacing={2}>
                                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.12em' }}>
                                        Resultado
                                    </Typography>

                                    <Paper
                                        sx={{
                                            p: { xs: 2, md: 3 },
                                            borderRadius: 3,
                                            background: `linear-gradient(145deg, ${alpha('#0d4f5c', 0.06)} 0%, ${alpha('#0d9488', 0.08)} 100%)`,
                                            border: `1px solid ${alpha('#0d9488', 0.25)}`,
                                        }}
                                    >
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Código / fórmula NHPdGD (referencia de liquidación)
                                        </Typography>
                                        <Typography
                                            component="div"
                                            sx={{
                                                fontFamily: '"Fraunces", Georgia, serif',
                                                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                                                fontWeight: 600,
                                                lineHeight: 1.45,
                                                wordBreak: 'break-word',
                                                color: 'text.primary',
                                            }}
                                        >
                                            {resultado.formula || '—'}
                                        </Typography>
                                    </Paper>

                                    <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                            <CheckCircleRoundedIcon color="success" />
                                            <Typography fontWeight={700}>Arancel estimado a facturar</Typography>
                                        </Stack>
                                        <Typography
                                            variant="h4"
                                            sx={{
                                                fontFamily: '"Fraunces", Georgia, serif',
                                                fontWeight: 700,
                                                color: 'success.dark',
                                                letterSpacing: '-0.02em',
                                            }}
                                        >
                                            {fmtMoney(resultado.arancel_total)}
                                        </Typography>
                                    </Paper>

                                    {Array.isArray(resultado.desglose_por_determinacion) && resultado.desglose_por_determinacion.length > 0 && (
                                        <Accordion
                                            defaultExpanded
                                            disableGutters
                                            elevation={0}
                                            sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, '&:before': { display: 'none' } }}
                                        >
                                            <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <ListAltRoundedIcon color="primary" fontSize="small" />
                                                    <Typography fontWeight={700}>Qué aporta cada estudio</Typography>
                                                </Stack>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ pt: 0 }}>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                    Cada fila es un estudio que cargaste arriba. Si pusiste varias veces el mismo, verás el total que suma al
                                                    cálculo (por código). Los espacios en el código se unifican.
                                                </Typography>
                                                <Stack spacing={2}>
                                                    {resultado.desglose_por_determinacion.map((bloque, bi) => (
                                                        <Paper
                                                            key={`${bloque.orden ?? bi}-${bloque.determinacion_id}-${bi}`}
                                                            variant="outlined"
                                                            sx={{ p: 2, borderRadius: 2 }}
                                                        >
                                                            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                                                                <Typography fontWeight={700}>{bloque.nombre}</Typography>
                                                                {bloque.veces != null && bloque.veces > 1 && (
                                                                    <Chip size="small" label={`${bloque.veces} veces`} color="primary" variant="outlined" />
                                                                )}
                                                            </Stack>
                                                            {Array.isArray(bloque.lineas) && bloque.lineas.length > 0 ? (
                                                                <TableContainer>
                                                                    <Table size="small">
                                                                        <TableHead>
                                                                            <TableRow>
                                                                                <TableCell>Código</TableCell>
                                                                                <TableCell align="right">Por una vez</TableCell>
                                                                                <TableCell align="right">Total que suma</TableCell>
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {bloque.lineas.map((ln, i) => (
                                                                                <TableRow key={`${bloque.determinacion_id}-${i}`}>
                                                                                    <TableCell sx={{ fontFamily: 'monospace' }}>{ln.codigo}</TableCell>
                                                                                    <TableCell align="right">{ln.cantidad}</TableCell>
                                                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                                                        {ln.cantidad_total != null ? ln.cantidad_total : ln.cantidad}
                                                                                    </TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </TableBody>
                                                                    </Table>
                                                                </TableContainer>
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Sin líneas de código
                                                                </Typography>
                                                            )}
                                                        </Paper>
                                                    ))}
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>
                                    )}

                                    {resultado.cantidad_por_codigo && Object.keys(resultado.cantidad_por_codigo).length > 0 && (
                                        <Paper sx={{ p: 2, borderRadius: 2 }}>
                                            <Typography fontWeight={700} sx={{ mb: 1 }}>
                                                Códigos acumulados
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                                Suma global por código. Ej. solo 40.01 → 40.01×1; 10×40.01 con otros códigos sigue reglas 1.03/1.05.
                                            </Typography>
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Código</TableCell>
                                                            <TableCell align="right">Cantidad total</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {Object.entries(resultado.cantidad_por_codigo)
                                                            .sort(([a], [b]) => a.localeCompare(b, 'es'))
                                                            .map(([cod, qty]) => (
                                                                <TableRow key={cod} hover>
                                                                    <TableCell sx={{ fontWeight: 600 }}>{cod}</TableCell>
                                                                    <TableCell align="right">{qty}</TableCell>
                                                                </TableRow>
                                                            ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Paper>
                                    )}

                                    {Array.isArray(resultado.lineas) && resultado.lineas.length > 0 && (
                                        <Paper sx={{ p: 2, borderRadius: 2 }}>
                                            <Typography fontWeight={700} sx={{ mb: 1 }}>
                                                Cómo se arma el monto
                                            </Typography>
                                            <Stack spacing={1.5}>
                                                {resultado.lineas.map((linea, i) => (
                                                    <Box
                                                        key={i}
                                                        sx={{
                                                            p: 1.5,
                                                            borderRadius: 2,
                                                            bgcolor: alpha(theme.palette.primary.main, 0.06),
                                                            borderLeft: `4px solid ${alpha('#0d9488', 0.8)}`,
                                                        }}
                                                    >
                                                        <Typography variant="subtitle2" fontWeight={700}>
                                                            {linea.titulo}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {linea.detalle}
                                                        </Typography>
                                                        <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mt: 0.5 }}>
                                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                                {linea.formula}
                                                            </Typography>
                                                            <Typography fontWeight={700}>{fmtMoney(linea.monto)}</Typography>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Paper>
                                    )}
                                </Stack>
                            )}
                        </Box>
                    </Fade>
                </Stack>
            </Box>
        </Layout>
    );
}

export default NhpdgdPage;
